import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import CryptoHoldings from './components/CryptoHoldings';
import SpotTradingForms from './components/SpotTradingForms';
import CFDTradingForms from './components/CFDTradingForms';
import FuturesTradingForms from './components/FuturesTradingForms';
import FuturesMyOrders from './components/FuturesMyOrders';
import TradingChart from './components/TradingChart';
import Markets from './components/Markets';
import SpotMyOrders from './components/SpotMyOrders';
import ArbitrageRobotPage from './components/ArbitrageRobotPage';
import EventBettingPage from './components/EventBettingPage';
import PropFirmChallengePage from './components/PropFirmChallengePage';
import WalletPage from './components/WalletPage';
import ProfilePage from './components/ProfilePage';
import OrderBook from './components/OrderBook';
import StakingPage from './pages/StakingPage';
import HomePage from './pages/HomePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import TwoFactorVerificationPage from './pages/TwoFactorVerificationPage';
import LoadingScreen from './components/LoadingScreen';
import AuthModal from './components/AuthModal';
import { useAuth } from './hooks/useAuth';
import { useDatabase } from './hooks/useDatabase';
import { useBybitData } from './hooks/useBybitData';
import { useBybitWebSocket } from './hooks/useBybitWebSocket';
import { useUserAssets } from './hooks/useUserAssets';
import { useFuturesTrading } from './hooks/useFuturesTrading';
import { usePropFirmTrading } from './hooks/usePropFirmTrading';
import { supabase } from './lib/supabaseClient';
import { isSupabaseConfigured } from './lib/supabaseClient';
import SwapCryptoPage from './components/SwapCryptoPage';

export type TradingMode = 'home' | 'swap' | 'futures' | 'cfd' | 'prop_firm' | 'robot' | 'events' | 'wallet' | 'profile' | 'staking';

export interface FuturesPosition {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  amount: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  marginType: 'isolated' | 'cross';
  liquidationPrice: number;
  unrealizedPnl: number;
  margin: number;
  roi: number;
  created_at: string;
  user_email?: string;
}

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'trade' | 'robot_profit' | 'binary_trade' | 'stake' | 'staking_profit' | 'staking_return' | 'challenge_fee' | 'challenge_reward';
  amount: number;
  description: string;
  status: 'completed' | 'pending' | 'failed';
  timestamp: string;
}

// User status tiers based on portfolio value in USD
export type UserStatus = 'No-Coiner' | 'Shrimp' | 'Crab' | 'Octopus' | 'Dolphin' | 'Shark' | 'Whale' | 'Humpback';

function App() {
  const { user, loading: authLoading, signOut } = useAuth();
  const { 
    balances, 
    updateBalances, 
    robotState, 
    updateRobotState, 
    marketData, 
    fetchMarketData, 
    loading: dbLoading, 
    transactions, 
    addTransaction,
    fetchTransactions,
    userStakes,
    addUserStake,
    eventOutcomes,
    eventBets,
    events,
    addEventBet,
    newsItems,
    portfolioSnapshots,
    createPortfolioSnapshot,
    fetchPortfolioSnapshots,
    kycStatus,
    updateKycStatus,
    referralCode,
    referralCount,
    referredUsers,
    fetchUserStakes,
    calculateCurrentEarnings,
    cancelUserStake,
    coingeckoMarketCapData
  } = useDatabase();
  const { assets, fetchAssets, updateAssetBalance } = useUserAssets();
  const { 
    activePositions, 
    openOrders, 
    fetchActivePositions, 
    fetchOpenOrders, 
    openPosition, 
    closePosition, 
    cancelOrder, 
    cancelAllOpenOrders 
  } = useFuturesTrading();
  
  const {
    propPositions,
    propOrders,
    propPositionHistory,
    propChallengeAccount,
    fetchPropPositions,
    fetchPropOrders,
    fetchPropPositionHistory,
    fetchPropChallengeAccount,
    initializeChallenge,
    cancelChallenge,
    placePropOrder,
    closePropPosition,
    cancelPropOrder,
    cancelAllPropOrders,
    closeAllPropPositions,
    loading: propFirmLoading
  } = usePropFirmTrading();
  
  const [tradingMode, setTradingMode] = useState<TradingMode>('home');
  const [selectedPair, setSelectedPair] = useState('BTCUSDT');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Interval reference for position refresh
  const positionRefreshIntervalRef = useRef<number | null>(null);
  
  // Collect all active symbols for WebSocket subscription
  const activeSymbols = useMemo(() => {
    const symbols = [...new Set([selectedPair, ...activePositions.map(p => p.symbol), ...propPositions.map(p => p.symbol)])];
    
    console.log('Active symbols for WebSocket subscription:', symbols);
    return symbols;
  }, [selectedPair, activePositions, propPositions]);
  
  // Get market data from Bybit API
  const { marketData: bybitMarketData, loading: bybitLoading } = useBybitData([selectedPair]);
  
  // Get real-time ticker data from WebSocket for all active symbols
  const { ticker, tickers } = useBybitWebSocket(activeSymbols);
  
  // Get current price from ticker or market data
  const currentSelectedPairPrice = ticker?.price ||
                                  bybitMarketData.find(data => data.symbol === selectedPair)?.price ||
                                  marketData.find(data => data.symbol === selectedPair)?.price ||
                                  0;


  // Calculate total portfolio value
 const totalPortfolioValue = useMemo(() => {
  let total = balances.usdt_balance; // Start with USDT balance

  // Get the current BTCUSDT price specifically
  const btcUsdtPrice = tickers['BTCUSDT']?.price ||
                       marketData.find(data => data.symbol === 'BTCUSDT')?.price ||
                       0;

  total += balances.btc_balance * btcUsdtPrice; // Add BTC value

  // Add value of other user assets
  assets.forEach(asset => {
    // Skip USDT and BTC as they are handled above
    if (asset.asset_symbol !== 'USDT' && asset.asset_symbol !== 'BTC') {
      // Construct the USDT pair symbol (e.g., 'ETHUSDT')
      const assetUsdtSymbol = `${asset.asset_symbol}USDT`;
      
      // Get the price from real-time tickers or fallback to marketData
      const assetPrice = tickers[assetUsdtSymbol]?.price ||
                         marketData.find(data => data.symbol === assetUsdtSymbol)?.price ||
                         0;
      
      total += asset.balance * assetPrice;
    }
  });

  return total;
}, [balances, assets, tickers, marketData]);

  // Function to determine user status based on portfolio value in USD
  const getUserStatus = useCallback((portfolioValue: number): UserStatus => {
    if (portfolioValue <= 0) return 'No-Coiner';
    if (portfolioValue < 10000) return 'Shrimp';
    if (portfolioValue < 25000) return 'Crab';
    if (portfolioValue < 50000) return 'Octopus';
    if (portfolioValue < 100000) return 'Dolphin';
    if (portfolioValue < 500000) return 'Shark';
    if (portfolioValue < 2000000) return 'Whale';
    return 'Humpback';
  }, []);

  // Get user status
  const userStatus = getUserStatus(totalPortfolioValue);

  // Initialize app data - only when user is authenticated
  useEffect(() => {
    const initializeData = async () => {
      if (!isSupabaseConfigured()) {
        console.warn('Supabase not configured, skipping data initialization');
        setIsLoading(false);
        return;
      }

      // Only fetch user-specific data if user is authenticated
      if (user) {
        await fetchMarketData();
        await fetchTransactions();
        await fetchAssets();
        await fetchPortfolioSnapshots();
        await fetchUserStakes();
        await fetchActivePositions();
        await fetchOpenOrders();
      } else {
        // For unauthenticated users, only fetch public market data
        await fetchMarketData();
      }
      setIsLoading(false);
    };
    
    if (!authLoading && !dbLoading) {
      initializeData();
    }
  }, [
    user, 
    authLoading, 
    dbLoading, 
    fetchMarketData, 
    fetchTransactions, 
    fetchAssets, 
    fetchPortfolioSnapshots, 
    fetchUserStakes,
    fetchActivePositions,
    fetchOpenOrders
  ]);

  // Set up periodic refresh of active positions - only for authenticated users
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      return;
    }

    // Only set up the interval if the user is logged in
    if (user) {
      // Clear any existing interval first
      if (positionRefreshIntervalRef.current) {
        clearInterval(positionRefreshIntervalRef.current);
      }
      
      // Set up a new interval to refresh positions every 5 seconds
      positionRefreshIntervalRef.current = window.setInterval(async () => {
        console.log('Refreshing active positions and prop positions...');
        
        try {
          // Refresh futures positions
          await fetchActivePositions();
          
          // Refresh prop firm positions if there's an active challenge
          if (propChallengeAccount) {
            await fetchPropPositions(propChallengeAccount.challengeId);
          }
        } catch (error) {
          console.error('Error refreshing positions:', error);
        }
      }, 5000); // 5 seconds interval
      
      // Clean up the interval when the component unmounts
      return () => {
        if (positionRefreshIntervalRef.current) {
          clearInterval(positionRefreshIntervalRef.current);
          positionRefreshIntervalRef.current = null;
        }
      };
    }
  }, [user, fetchActivePositions, fetchPropPositions, propChallengeAccount]);

  // Handle futures trade
  const handleFuturesTrade = useCallback(async (
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType: 'market' | 'limit' = 'market'
  ) => {
    try {
      // Use the openPosition function from useFuturesTrading hook
      const positionId = await openPosition({
        symbol: selectedPair,
        side,
        amount,
        leverage,
        marginType,
        orderType,
       price: currentSelectedPairPrice, // Always pass the current price
        stopLoss: stopLoss?.trigger_price,
        takeProfit: takeProfit?.trigger_price
      });
      
      if (positionId) {
        // If it's a market order, add a transaction record
        if (orderType === 'market') {
          // Calculate margin
          const notionalValue = amount * currentSelectedPairPrice;
          const requiredMargin = notionalValue / leverage;
          
          // Add transaction record
          await addTransaction({
            type: 'trade',
            amount: -requiredMargin,
            description: `Opened ${side} position for ${selectedPair} with ${leverage}x leverage`,
            status: 'completed'
          });
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in handleFuturesTrade:', error);
      return false;
    }
  }, [currentSelectedPairPrice, selectedPair, openPosition, addTransaction]);

  // Handle closing a futures position
  const handleClosePosition = useCallback(async (positionId: string) => {
    try {
      // Call the closePosition function from useFuturesTrading hook
      const success = await closePosition(positionId);
      
      if (success) {
        // The closePosition function will handle updating the database and refreshing the positions
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in handleClosePosition:', error);
      return false;
    }
  }, [closePosition]);

  // Handle prop challenge trade
  const handlePropChallengeTrade = useCallback(async (
    challengeId: string,
    symbol: string,
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType: 'market' | 'limit' = 'market'
  ) => {
    try {
      console.log(`Placing prop challenge order for ${symbol}, side: ${side}, amount: ${amount}, leverage: ${leverage}`);
      
      // Use the placePropOrder function from usePropFirmTrading hook
      const success = await placePropOrder({
        challengeId,
        symbol,
        side: side === 'long' ? 'buy' : 'sell',
        amount,
        leverage,
        marginType,
        orderType,
        price: orderType === 'limit' ? currentSelectedPairPrice : undefined,
        stopLoss: stopLoss?.trigger_price,
        takeProfit: takeProfit?.trigger_price
      });
      
      if (success) {
        console.log('Prop challenge order placed successfully');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in handlePropChallengeTrade:', error);
      return false;
    }
  }, [currentSelectedPairPrice, placePropOrder]);

  // Handle spot order
  const handleSpotOrder = useCallback(async (order: {
    side: 'buy' | 'sell';
    order_type: 'market' | 'limit';
    amount: number;
    price?: number;
    stop_loss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number };
    take_profit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number };
  }) => {
    const { side, order_type, amount } = order;
    const price = order.price || currentSelectedPairPrice;
    
    // Calculate total cost/proceeds
    const total = amount * price;
    
    if (side === 'buy') {
      // Check if user has enough USDT
      if (total > balances.usdt_balance) {
        alert('Insufficient USDT balance');
        return;
      }
      
      // Update balances
      await updateBalances({ 
        usdt_balance: balances.usdt_balance - total,
        btc_balance: balances.btc_balance + amount
      });
      
      // Add transaction
      await addTransaction({
        type: 'trade',
        amount: -total,
        description: `Bought ${amount.toFixed(6)} BTC at ${price.toFixed(2)} USDT`,
        status: 'completed'
      });
    } else {
      // Check if user has enough BTC
      if (amount > balances.btc_balance) {
        alert('Insufficient BTC balance');
        return;
      }
      
      // Update balances
      await updateBalances({ 
        usdt_balance: balances.usdt_balance + total,
        btc_balance: balances.btc_balance - amount
      });
      
      // Add transaction
      await addTransaction({
        type: 'trade',
        amount: total,
        description: `Sold ${amount.toFixed(6)} BTC at ${price.toFixed(2)} USDT`,
        status: 'completed'
      });
    }
  }, [currentSelectedPairPrice, balances, updateBalances, addTransaction]);

  // Handle swap between cryptocurrencies
  const handleSwap = useCallback(async (fromCurrency: string, toCurrency: string, amount: number) => {
    try {
      // Get current prices
      const fromPrice = fromCurrency === 'BTC' ? currentSelectedPairPrice : 1; // Assuming USDT price is 1
      const toPrice = toCurrency === 'BTC' ? currentSelectedPairPrice : 1;
      
      // Calculate conversion
      const fromValue = amount * fromPrice;
      const toAmount = fromValue / toPrice;
      
      // Check balances
      if (fromCurrency === 'BTC' && amount > balances.btc_balance) {
        throw new Error('Insufficient BTC balance');
      } else if (fromCurrency === 'USDT' && amount > balances.usdt_balance) {
        throw new Error('Insufficient USDT balance');
      }
      
      // Update balances
      if (fromCurrency === 'BTC' && toCurrency === 'USDT') {
        await updateBalances({
          btc_balance: balances.btc_balance - amount,
          usdt_balance: balances.usdt_balance + toAmount
        });
      } else if (fromCurrency === 'USDT' && toCurrency === 'BTC') {
        await updateBalances({
          usdt_balance: balances.usdt_balance - amount,
          btc_balance: balances.btc_balance + toAmount
        });
      } else {
        // Handle other asset pairs using user_assets
        const fromAsset = assets.find(a => a.asset_symbol === fromCurrency);
        if (!fromAsset || fromAsset.balance < amount) {
          throw new Error(`Insufficient ${fromCurrency} balance`);
        }
        
        // Update asset balances
        await updateAssetBalance(fromCurrency, fromAsset.balance - amount);
        
        const toAsset = assets.find(a => a.asset_symbol === toCurrency);
        if (toAsset) {
          await updateAssetBalance(toCurrency, toAsset.balance + toAmount);
        } else {
          await updateAssetBalance(toCurrency, toAmount);
        }
      }
      
      // Add transaction record for swap
      await addTransaction({
        type: 'trade',
        amount: -amount * fromPrice, // Negative amount for the source currency
        description: `Swapped ${amount} ${fromCurrency} to ${toAmount.toFixed(6)} ${toCurrency}`,
        status: 'completed'
      });
      
      return true;
    } catch (error) {
      console.error('Swap error:', error);
      return false;
    }
  }, [currentSelectedPairPrice, balances, updateBalances, addTransaction, assets, updateAssetBalance]);

  // Handle wallet operations (deposit/withdrawal)
  const handleWalletOperation = useCallback(async (type: 'deposit' | 'withdrawal', currency: 'USDT' | 'BTC', amount: number) => {
    if (type === 'deposit') {
      if (currency === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance + amount });
      } else {
        await updateBalances({ btc_balance: balances.btc_balance + amount });
      }
      
      // Add transaction
      await addTransaction({
        type: 'deposit',
        amount,
        description: `Deposited ${amount} ${currency}`,
        status: 'completed'
      });
    } else {
      // Check balance
      if (currency === 'USDT' && amount > balances.usdt_balance) {
        alert('Insufficient USDT balance');
        return;
      } else if (currency === 'BTC' && amount > balances.btc_balance) {
        alert('Insufficient BTC balance');
        return;
      }
      
      if (currency === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance - amount });
      } else {
        await updateBalances({ btc_balance: balances.btc_balance - amount });
      }
      
      // Add transaction
      await addTransaction({
        type: 'withdrawal',
        amount: -amount,
        description: `Withdrawn ${amount} ${currency}`,
        status: 'completed'
      });
    }
  }, [balances, updateBalances, addTransaction]);

  // Handle staking
  const handleStake = useCallback(async (asset: string, amount: number, duration: number) => {
    try {
      // Check minimum stake
      if (amount < 0.001) {
        setStakingMessage({
          type: 'error',
          text: `Minimum stake amount is 0.001 ${asset}`
        });
        return;
      }
      
      // Check balance
      if (asset === 'USDT' && amount > balances.usdt_balance) {
        throw new Error(`Insufficient ${asset} balance`);
      } else if (asset === 'BTC' && amount > balances.btc_balance) {
        throw new Error(`Insufficient ${asset} balance`);
      }
      
      // Check if user already has an active stake for this asset
      if (hasActiveStakeForAsset(asset)) {
        throw new Error(`You already have an active stake for ${asset}. Please wait for it to complete before creating a new one.`);
      }
      
      // Calculate APY based on duration
      let apy = 5.0; // Base APY
      if (duration >= 90) apy = 7.5;
      if (duration >= 180) apy = 10.0;
      if (duration >= 365) apy = 12.5;
      
      setIsStaking(true);
      
      // Call the addUserStake function
      const success = await addUserStake(
        asset,
        amount,
        apy,
        duration
      );
      
      if (success) {
        setStakingMessage({
          type: 'success',
          text: `Successfully staked ${amount} ${asset} for ${duration} days`
        });
        
        // Close modal and reset form
        setShowConfirmationModal(false);
        setStakingAmount('');
        setSelectedAsset(null);
        
        // Call the onStake prop to update balances
        await onStake(asset, amount, duration);
        
        // Refresh user stakes
        await fetchUserStakes();
      } else {
        throw new Error('Staking failed');
      }
    } catch (error: any) {
      setStakingMessage({
        type: 'error',
        text: error.message || 'Failed to stake. Please try again.'
      });
    } finally {
      setIsStaking(false);
    }
  }, [balances, updateBalances, addTransaction, addUserStake, fetchUserStakes]);

  // Calculate time remaining for a stake
  const calculateTimeRemaining = (endDate: string) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();
    
    if (diff <= 0) return 'Completed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return `${days}d ${hours}h remaining`;
  };

  // Calculate progress percentage for a stake
  const calculateProgress = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    if (elapsed <= 0) return 0;
    if (elapsed >= total) return 100;
    
    return (elapsed / total) * 100;
  };

  // Get active stakes
  const activeStakes = userStakes.filter(stake => stake.status === 'active');
  
  // Get completed stakes
  const completedStakes = userStakes.filter(stake => stake.status === 'completed');

  // Show loading state while checking for existing challenge
  if (propFirmLoading && propChallengeAccount === null) {
    return <LoadingScreen />;
  }

  // Set default pair when changing trading mode
  const handleTradingModeChange = (mode: TradingMode) => {
    setTradingMode(mode);
    
    // Set default pair based on trading mode
    if (mode === 'futures') {
      setSelectedPair('BTCUSDT');
    } else if (mode === 'cfd') {
      setSelectedPair('EURUSD');
    }
  };

  return (
    <Router>
      <div className="min-h-screen bg-slate-900 text-white">
        <Routes>
          <Route path="/sign-in" element={user ? <Navigate to="/" /> : <SignInPage />} />
          <Route path="/sign-up" element={user ? <Navigate to="/" /> : <SignUpPage />} />
          <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
          <Route path="/reset-password" element={user ? <Navigate to="/" /> : <ResetPasswordPage />} />
          <Route path="/email-verification" element={user ? <Navigate to="/" /> : <EmailVerificationPage />} />
          <Route path="/email-confirmation" element={<EmailConfirmationPage />} />
          <Route path="/two-factor-verification" element={<TwoFactorVerificationPage />} />
          <Route path="*" element={
            user ? (
              <>
                <Header 
                  tradingMode={tradingMode} 
                  setTradingMode={handleTradingModeChange}
                  selectedPair={selectedPair}
                  setSelectedPair={setSelectedPair}
                  currentPrice={currentSelectedPairPrice}
                  marketData={marketData.find(data => data.symbol === selectedPair)}
                  usdtBalance={balances.usdt_balance}
                  btcBalance={balances.btc_balance}
                  totalPortfolioValue={totalPortfolioValue}
                  user={user}
                  signOut={signOut}
                  marketDataList={marketData || []}
                  ticker={ticker}
                  userStatus={userStatus}
                />
                
                <main>
                  {tradingMode === 'home' && (
                    <HomePage 
                      currentBtcPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      marketData={marketData || []}
                      futuresPositions={activePositions}
                      transactions={transactions}
                      setTradingMode={setTradingMode}
                      newsItems={newsItems}
                      totalPortfolioValue={totalPortfolioValue}
                      userAssets={assets}
                    />
                  )}
                  
                  {tradingMode === 'swap' && (
                    <SwapCryptoPage
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentBtcPrice={currentSelectedPairPrice}
                      onSwap={handleSwap}
                    />
                  )}
                  
                  {tradingMode === 'futures' && (
                    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-900 overflow-y-auto">
                      {/* Left Column - Order Book (full width on mobile, fixed width on desktop) */}
                      <div className="w-full lg:w-80 border-b lg:border-r lg:border-b-0 border-slate-700 h-auto lg:h-full">
                        <OrderBook 
                          currentPrice={currentSelectedPairPrice}
                          selectedPair={selectedPair}
                          orderBook={{ bids: [], asks: [], lastUpdateId: 0 }}
                          tradingMode="futures"
                        />
                      </div>
                      
                      {/* Middle Column - Chart and Trading Forms */}
                      <div className="flex-1 flex flex-col">
                        {/* Trading Chart */}
                        <div className="lg:flex-1">
                          <TradingChart selectedPair={selectedPair} />
                        </div>
                        
                        {/* Trading Forms and Positions */}
                        <div className="flex-1 flex flex-col">
                          {/* Trading Forms */}
                          <div>
                            <FuturesTradingForms 
                              currentBtcPrice={currentSelectedPairPrice}
                             currentPrice={currentSelectedPairPrice}
                              usdtBalance={balances.usdt_balance}
                              onFuturesTrade={handleFuturesTrade}
                              selectedPair={selectedPair}
                            />
                          </div>
                          
                          {/* Positions - This div will now expand to fill available space */}
                          <div className="border-t border-slate-700 flex-1">
                            <FuturesMyOrders 
                              currentBtcPrice={currentSelectedPairPrice}
                              futuresPositions={activePositions}
                              marketData={marketData || []}
                              onClosePosition={handleClosePosition}
                              openOrders={openOrders}
                              onCancelOrder={cancelOrder}
                              onCancelAllOrders={cancelAllOpenOrders}
                              updateBalances={updateBalances}
                              ticker={ticker}
                              tickers={tickers}
                            />
                          </div>
                        </div>
                      </div>
                    
                      
                      {/* Right Column - Markets (full width on mobile, fixed width on desktop) */}
                      <div className="w-full lg:w-80 border-t lg:border-l lg:border-t-0 border-slate-700 h-auto lg:h-full">
                        <Markets 
                          marketData={marketData || []}
                          selectedPair={selectedPair}
                          setSelectedPair={setSelectedPair}
                          currentPrice={currentSelectedPairPrice}
                          tradingMode="futures"
                          tickers={tickers}
                          coingeckoMarketCapData={coingeckoMarketCapData}
                        />
                      </div>
                    </div>
                  )}
                  
                  {tradingMode === 'cfd' && (
                    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-900 overflow-y-auto">
                      {/* Left Column - Order Book (full width on mobile, fixed width on desktop) */}
                      <div className="w-full lg:w-80 border-b lg:border-r lg:border-b-0 border-slate-700 h-auto lg:h-full">
                        <OrderBook 
                          currentPrice={currentSelectedPairPrice}
                          selectedPair={selectedPair}
                          orderBook={{ bids: [], asks: [], lastUpdateId: 0 }}
                          tradingMode="cfd"
                        />
                      </div>
                      
                      {/* Middle Column - Chart and Trading Forms */}
                      <div className="flex-1 flex flex-col bg-slate-900">
                        {/* Trading Chart */}
                        <div className="lg:flex-1">
                          <TradingChart selectedPair={selectedPair} />
                        </div>
                        
                        {/* Trading Forms */}
                        <div>
                          <CFDTradingForms 
                            currentPrice={currentSelectedPairPrice}
                            usdtBalance={balances.usdt_balance}
                            selectedPair={selectedPair}
                            onCFDTrade={handleFuturesTrade}
                          />
                        </div>
                        
                        {/* Positions */}
                        <div className="border-t border-slate-700 flex-1">
                          <FuturesMyOrders 
                            currentBtcPrice={currentSelectedPairPrice}
                            futuresPositions={activePositions}
                            marketData={marketData || []}
                            onClosePosition={handleClosePosition}
                            openOrders={openOrders}
                            onCancelOrder={cancelOrder}
                            onCancelAllOrders={cancelAllOpenOrders}
                            updateBalances={updateBalances}
                            ticker={ticker}
                            tickers={tickers}
                          />
                        </div>
                      </div>
                      
                      {/* Right Column - Markets (full width on mobile, fixed width on desktop) */}
                      <div className="w-full lg:w-80 border-t lg:border-l lg:border-t-0 border-slate-700 h-auto lg:h-full">
                        <Markets 
                          marketData={marketData || []}
                          selectedPair={selectedPair}
                          setSelectedPair={setSelectedPair}
                          currentPrice={currentSelectedPairPrice}
                          tradingMode="cfd"
                          tickers={tickers}
                          coingeckoMarketCapData={coingeckoMarketCapData}
                        />
                      </div>
                    </div>
                  )}
                  
                  {tradingMode === 'prop_firm' && (
                    <PropFirmChallengePage 
                      currentPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      selectedPair={selectedPair}
                      setSelectedPair={setSelectedPair}
                      marketData={marketData || []}
                      onUpdateBalances={updateBalances}
                      onAddTransaction={addTransaction}
                      user={user}
                      robotState={robotState}
                      onUpdateRobotState={updateRobotState}
                      propPositions={propPositions}
                      propOrders={propOrders}
                      propPositionHistory={propPositionHistory}
                      onPropChallengeTrade={handlePropChallengeTrade}
                      onClosePropPosition={closePropPosition}
                      onCancelPropOrder={cancelPropOrder}
                      onCancelAllPropOrders={cancelAllPropOrders}
                      closeAllPropPositions={closeAllPropPositions}
                      ticker={ticker}
                      tickers={tickers}
                    />
                  )}
                  
                  {tradingMode === 'robot' && (
                    <ArbitrageRobotPage 
                      currentBtcPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      robotState={robotState}
                      onUpdateRobotState={updateRobotState}
                      onUpdateBalances={updateBalances}
                    />
                  )}
                  
                  {tradingMode === 'events' && (
                    <EventBettingPage 
                      events={events}
                      eventOutcomes={eventOutcomes}
                      eventBets={eventBets}
                      usdtBalance={balances.usdt_balance}
                      onEventBet={addEventBet}
                    />
                  )}
                  
                  {tradingMode === 'wallet' && (
                    <WalletPage 
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      transactions={transactions}
                      currentBtcPrice={currentSelectedPairPrice}
                      onWalletOperation={handleWalletOperation}
                      kycStatus={kycStatus}
                      marketData={marketData || []}
                      setTradingMode={setTradingMode}
                      userAssets={assets}
                    />
                  )}
                  
                  {tradingMode === 'profile' && (
                    <ProfilePage 
                      user={user}
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentPrice={currentSelectedPairPrice}
                      onSignOut={signOut}
                      onUpdatePassword={async (password) => await supabase.auth.updateUser({ password })}
                      referralCode={referralCode}
                      referralCount={referralCount}
                      referredUsers={referredUsers}
                    />
                  )}
                  
                  {tradingMode === 'staking' && (
                    <StakingPage 
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentBtcPrice={currentSelectedPairPrice}
                      onStake={handleStake}
                    />
                  )}
                </main>
              </>
            ) : (
              <Navigate to="/sign-in" />
            )
          } />
        </Routes>
        
        {!user && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />}
      </div>
    </Router>
  );
}

export default App;
